/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/inviteUser.config.json";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { SEND_COMMITTE_MEMBER_INVITES, SendDiscount } from "../graphql";
import { VApplication } from "../../../application";
import notification from "../config/notifications.config.json";
import axios from "axios";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { pretty } from "@vlinder-web/utils-react";

const log: any = LoggerModule?.getInstance();
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface SendPurchaseInviteUserProps {
  presentation: any;
  section?: any;
  type?: any;
  userEmail?: any;
  count?: any;
  sectionOptType?: any;
  partition?: any;
}
export interface ISendPurchaseInviteMethods extends IPluginMethodMap {
  sendPurchaseInviteUser(args: SendPurchaseInviteUserProps): Promise<any>;
}

export class SendPurchaseInviteUserService implements IAgentPlugin {
  readonly methods: ISendPurchaseInviteMethods;

  constructor() {
    this.methods = {
      sendPurchaseInviteUser: this.sendPurchaseInviteUser.bind(this),
    };
  }

  async sendPurchaseInviteUser(args: SendPurchaseInviteUserProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const sendInvite = this.createInviteObjects(args);
    const isValid = this.validateArgs(args);
    if (isValid?.isValidate) {
      throw new Error(isValid?.errorMessage);
    }
    console.log(args, sendInvite)

    try {
      if (client) {
        const res = await client.mutate({
          mutation: SEND_COMMITTE_MEMBER_INVITES,
          variables: {
            data: sendInvite,
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          if (args?.isDiscountAdded) {
            const res1 = await client.mutate({
              mutation: SendDiscount,
              variables: {
                data: {
                  discountId: args?.discount,
                  count: parseInt(args?.discountCount || 0),
                  status: "AssignedToCommittee",
                  committeeId: res?.data?.sendCommitteMemberInvites?.user[0]?.id,
                },
              },
              fetchPolicy: "no-cache",
            });
            if (res1?.data) {
              return {
                ...res1?.data?.updateDiscountStatus,
              }
            }
            else {
              return {
                message: "Invite sent successfully, but discount failed to be added."
              }
            }
          }
        }
        return {
          ...res?.data?.sendPurchaseReferals,
        };
      }

    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      console.log(
        `_display_message?.extensions?.display_message`,
        _display_message?.extensions?.display_message
      );
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private createInviteObjects(data: any) {
    let {
      presentation,
      type,
      sections,
      userEmail,
      maxInvites,
      sectionOptType,
      partition,
      inviteType,
    } = data;
    const inviteObjects: any[] = [];
    userEmail.forEach((email: any) => {
      if (email === "openreg@gem.com") {
        inviteType = "Open";
      }
      const inviteObj = {
        email,
        presentation,
        partition,
        sections: sections ? sections : null,
        inviteType: inviteType ?? "Closed",
        maxInvites: parseInt(maxInvites, 10) || 0,
        sectionOptType,
        openRegEnabled: inviteType === "Open" ? true : false,
      };
      inviteObjects.push(inviteObj);
    });
    console.log("inviteObjects", inviteObjects);
    return inviteObjects;
  }

  private validateArgs(args: any): any {
    let message = {
      isValidate: false,
      errorMessage: "",
    };
    const {
      presentation,
      type,
      sections,
      userEmail,
      maxInvites,
      sectionOptType,
      partition,
      inviteType,
    } = args;

    if (maxInvites === "" || maxInvites === null || maxInvites === undefined) {
      message = {
        ...message,
        isValidate: true,
        errorMessage: "Invites Count cannot be Empty",
      };
      return message;
    }

    if (maxInvites && maxInvites) {
      const count = parseInt(maxInvites, 10) || 0;
      if (count < 0) {
        message = {
          ...message,
          isValidate: true,
          errorMessage: "Invites Count cannot be negative",
        };
        return message;
      }
    }

    if (
      presentation === "" ||
      presentation === null ||
      presentation === undefined
    ) {
      message = {
        ...message,
        isValidate: true,
        errorMessage: "Presentation cannot be Empty",
      };
      return message;
    }
    if (
      partition === "" ||
      partition === null ||
      partition === undefined ||
      partition === "-"
    ) {
      message = {
        ...message,
        isValidate: true,
        errorMessage: "Partition cannot be Empty",
      };
      return message;
    }
    if (
      sectionOptType === "" ||
      sectionOptType === null ||
      sectionOptType === undefined ||
      sectionOptType === "-"
    ) {
      message = {
        ...message,
        isValidate: true,
        errorMessage: "Section Option Type cannot be Empty",
      };
      return message;
    }
    if (partition === "Default" || partition === "SpecialReserve") {
      if (sections !== null && sections !== undefined) {
        message = {
          ...message,
          isValidate: true,
          errorMessage: `Section should be null for partition type ${partition}`,
        };
        return message;
      }
    }
    if (partition === "SectionSpecific") {
      if (
        sections === null ||
        sections === "" ||
        sections === "-" ||
        sections === undefined
      ) {
        message = {
          ...message,
          isValidate: true,
          errorMessage: `Section Id Should be not empty`,
        };
      }
    }
    return message;
  }
}
